import React from 'react';

import { AbstractDialogTab } from '../../../base/dialog';
import { translate } from '../../../base/i18n';
import { setTileLimitTileView } from '../../../filmstrip/actions.web';

/**
 * The type of the React {@code Component} props of {@link MoreTab}.
 */
export type Props = {
  ...$Exact<AbstractDialogTabProps>,

  /**
   * The currently selected language to display in the language select
   * dropdown.
   */
  currentLanguage: string,

  /**
   * Whether or not follow me is currently active (enabled by some other participant).
   */
  followMeActive: boolean,

  /**
   * Whether or not the user has selected the Follow Me feature to be enabled.
   */
  followMeEnabled: boolean,

  /**
   * All available languages to display in the language select dropdown.
   */
  languages: Array<string>,

  /**
   * Whether or not to display the language select dropdown.
   */
  showLanguageSettings: boolean,

  /**
   * Whether or not to display moderator-only settings.
   */
  showModeratorSettings: boolean,

  /**
   * Whether or not to display the prejoin settings section.
   */
  showPrejoinSettings: boolean,

  /**
   * Whether or not to show prejoin screen.
   */
  showPrejoinPage: boolean,

  /**
   * Whether or not the user has selected the Start Audio Muted feature to be
   * enabled.
   */
  startAudioMuted: boolean,

  /**
   * Whether or not the user has selected the Start Video Muted feature to be
   * enabled.
   */
  startVideoMuted: boolean,

  /**
   * Invoked to obtain translated strings.
   */
  t: Function,
};

/**
 * The type of the React {@code Component} state of {@link MoreTab}.
 */
type State = {

  /**
   * Whether or not the language select dropdown is open.
   */
  isLanguageSelectOpen: boolean,

  /**
   * Delete cache on checked.
   */
  deleteCache: boolean,

    /**
     * Disable Auto PIP.
     */
    disableAutoPip: boolean
};

/**
 * React {@code Component} for modifying language and moderator settings.
 * Built without any Atlaskit dependencies - uses plain HTML elements
 * styled via SCSS instead of Checkbox / DropdownMenu / DropdownItem.
 *
 * @augments Component
 */
class MoreTab extends AbstractDialogTab<Props, State> {
    /**
   * Initializes a new {@code MoreTab} instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
    constructor(props: Props) {
        super(props);

        this.state = {
            isLanguageSelectOpen: false,
            deleteCache: false,
            disableAutoPip: false
        };

        // Bind event handlers so they are only bound once for every instance.
        this._onLanguageDropdownToggle = this._onLanguageDropdownToggle.bind(this);
        this._onLanguageDropdownClose = this._onLanguageDropdownClose.bind(this);
        this._onLanguageSelect = this._onLanguageSelect.bind(this);
    }

    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const { showLanguageSettings, showPrejoinSettings } = this.props;
        const content = [];

        if (showPrejoinSettings) {
            content.push(this._renderPrejoinScreenSettings());
        }

        // if (showModeratorSettings) {
        //     content.push(this._renderModeratorSettings());
        // }

        if (showLanguageSettings) {
            content.push(this._renderLangaugeSelect());
        }

        content.push(this._renderAutoPIPDisable());

        return <div className = 'more-tab'>{content}</div>;
    }

    _onLanguageDropdownToggle: () => void;

    /**
   * Callback invoked to toggle display of the language select dropdown.
   *
   * @private
   * @returns {void}
   */
    _onLanguageDropdownToggle() {
        this.setState(prevState => {
            return { isLanguageSelectOpen: !prevState.isLanguageSelectOpen };
        });
    }

    _onLanguageDropdownClose: () => void;

    /**
   * Callback invoked to close the language select dropdown.
   *
   * @private
   * @returns {void}
   */
    _onLanguageDropdownClose() {
        this.setState({ isLanguageSelectOpen: false });
    }

    _onLanguageSelect: (string) => Function;

    /**
   * Creates a click handler for a given language option.
   *
   * @param {string} language - The language associated with the click event.
   * @private
   * @returns {Function}
   */
    _onLanguageSelect(language) {
        return e => {
            e.stopPropagation();
            this.setState({ isLanguageSelectOpen: false });
            super._onChange({ currentLanguage: language });
        };
    }

    /**
   * Returns the menu item for changing displayed language.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderLangaugeSelect() {
        const { currentLanguage, languages, t } = this.props;
        const { isLanguageSelectOpen } = this.state;

        const languageItems = languages.map(language => (
            <li
                aria-selected = { language === currentLanguage }
                className = 'custom-dropdown-item'
                key = { language }
                onClick = { this._onLanguageSelect(language) }
                role = 'option'>
                {t(`languages:${language}`)}
            </li>
        ));

        return (
            <div
                className = 'settings-sub-pane language-settings'
                key = 'language'>
                <div className = 'mock-atlaskit-label'>{t('settings.language')}</div>
                <div className = 'dropdown-menu1'>
                    <div className = { `custom-dropdown ${isLanguageSelectOpen ? 'open' : ''}` }>
                        <button
                            aria-expanded = { isLanguageSelectOpen }
                            aria-haspopup = 'listbox'
                            className = 'custom-dropdown-trigger'
                            onClick = { this._onLanguageDropdownToggle }
                            type = 'button'>
                            <span>{ currentLanguage ? t(`languages:${currentLanguage}`) : '' }</span>
                            <span className = 'custom-dropdown-caret' />
                        </button>
                        {isLanguageSelectOpen && (
                            <>
                                <div
                                    className = 'custom-dropdown-backdrop'
                                    onClick = { this._onLanguageDropdownClose } />
                                <ul
                                    className = 'custom-dropdown-menu'
                                    role = 'listbox'>
                                    {languageItems}
                                </ul>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Returns the React Element for modifying auto PIP disable settings.
     *
     * @private
     * @returns {ReactElement}
     */
    _renderAutoPIPDisable() {
        const { disableAutoPip, t } = this.props;

        return (
            <div
                className = 'settings-sub-pane'
                key = 'auto-pip-disable'>
                <div className = 'mock-atlaskit-label'>{t('settingsView.autoPip')}</div>
                <label className = 'custom-checkbox-wrapper'>
                    <input
                        checked = { disableAutoPip }
                        className = 'custom-checkbox-input'
                        name = 'auto-pip-disable'
                        onChange = { ({ target: { checked } }) =>
                            super._onChange({ disableAutoPip: checked })
                        }
                        type = 'checkbox' />
                    <span className = 'custom-checkbox-box' />
                    <span className = 'custom-checkbox-label'>{ t('settingsView.disableAutoPip') }</span>
                </label>
            </div>
        );
    }

    /**
   * Returns the React Element for modifying tile view tile limit settings.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderTileViewLimitSelector() {
        return (
            <div
                className = 'settings-sub-pane'
                key = 'tile-view-limit'>
                <div className = 'mock-atlaskit-label'>{this.props.t('settings.tileViewLimit')}</div>
                <input
                    className = 'custom-number-input'
                    id = 'tile-view-limit-input'
                    min = { 2 }
                    onChange = { e =>
                        APP.store.dispatch(setTileLimitTileView(e.target.value))
                    }
                    type = 'number' />
            </div>
        );
    }

    /**
   * Returns the React Element for modifying conference-wide settings.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderModeratorSettings() {
        const {
            followMeActive,
            followMeEnabled,
            startAudioMuted,
            startVideoMuted,
            t
        } = this.props;

        return (
            <div
                className = 'settings-sub-pane'
                key = 'moderator'>
                <div className = 'mock-atlaskit-label'>{t('settings.moderator')}</div>
                <label className = 'custom-checkbox-wrapper'>
                    <input
                        checked = { startAudioMuted }
                        className = 'custom-checkbox-input'
                        name = 'start-audio-muted'
                        onChange = { ({ target: { checked } }) =>
                            super._onChange({ startAudioMuted: checked })
                        }
                        type = 'checkbox' />
                    <span className = 'custom-checkbox-box' />
                    <span className = 'custom-checkbox-label'>{ t('settings.startAudioMuted') }</span>
                </label>
                <label className = 'custom-checkbox-wrapper'>
                    <input
                        checked = { startVideoMuted }
                        className = 'custom-checkbox-input'
                        name = 'start-video-muted'
                        onChange = { ({ target: { checked } }) =>
                            super._onChange({ startVideoMuted: checked })
                        }
                        type = 'checkbox' />
                    <span className = 'custom-checkbox-box' />
                    <span className = 'custom-checkbox-label'>{ t('settings.startVideoMuted') }</span>
                </label>
                <label className = { `custom-checkbox-wrapper ${followMeActive ? 'disabled' : ''}` }>
                    <input
                        checked = { followMeEnabled && !followMeActive }
                        className = 'custom-checkbox-input'
                        disabled = { followMeActive }
                        name = 'follow-me'
                        onChange = { ({ target: { checked } }) => {
                            super._onChange({ followMeEnabled: checked });
                        } }
                        type = 'checkbox' />
                    <span className = 'custom-checkbox-box' />
                    <span className = 'custom-checkbox-label'>{ t('settings.followMe') }</span>
                </label>
            </div>
        );
    }

    /**
   * Returns the React Element for modifying prejoin screen settings.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderPrejoinScreenSettings() {
        const { t, deleteCache } = this.props;

        return (
            <div
                className = 'settings-sub-pane'
                key = 'prejoin-screen'>
                <div className = 'mock-atlaskit-label'>{t('prejoin.premeeting')}</div>
                {/* <label className = 'custom-checkbox-wrapper'>
                    <input
                        checked = { showPrejoinPage }
                        className = 'custom-checkbox-input'
                        name = 'show-prejoin-page'
                        onChange = {
                            ({ target: { checked } }) =>
                                super._onChange({ showPrejoinPage: checked })
                        }
                        type = 'checkbox' />
                    <span className = 'custom-checkbox-box' />
                    <span className = 'custom-checkbox-label'>{ t('prejoin.showScreen') }</span>
                </label> */}
                <label className = 'custom-checkbox-wrapper'>
                    <input
                        checked = { deleteCache }
                        className = 'custom-checkbox-input'
                        name = 'delete-cache'
                        onChange = { ({ target: { checked } }) =>
                            super._onChange({ deleteCache: checked })
                        }
                        type = 'checkbox' />
                    <span className = 'custom-checkbox-box' />
                    <span className = 'custom-checkbox-label'>{ t('dialog.deleteCache') }</span>
                </label>
            </div>
        );
    }
}

export default translate(MoreTab);
